import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { US_STATES } from "@/lib/usStates";

/**
 * Reusable structured US-address field group: Street, City, State (dropdown), ZIP.
 *
 * Renders four inputs side-by-side on wider screens and stacks on mobile.
 *
 * Props:
 *  - value: { address, city, state, zip_code }
 *  - onChange(patch): receives a partial object to merge into parent state
 *  - showLabels: whether to render <Label> above each field (default true)
 *  - testIdPrefix: prefix for data-testid attributes (default "address")
 *  - streetLabel: override label for the street field
 *  - className: outer wrapper class
 *  - disabled
 */
export default function AddressFields({
  value = {},
  onChange,
  showLabels = true,
  testIdPrefix = "address",
  streetLabel = "Street address",
  className = "",
  disabled = false,
}) {
  const v = value || {};
  const patch = (k, val) => onChange?.({ [k]: val });
  return (
    <div className={`space-y-3 ${className}`}>
      <div>
        {showLabels && <Label>{streetLabel}</Label>}
        <Input
          className="rounded-sm"
          value={v.address || ""}
          onChange={(e) => patch("address", e.target.value)}
          placeholder="123 Main St"
          data-testid={`${testIdPrefix}-street`}
          disabled={disabled}
          autoComplete="address-line1"
        />
      </div>
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-12 sm:col-span-6">
          {showLabels && <Label>City / Town</Label>}
          <Input
            className="rounded-sm"
            value={v.city || ""}
            onChange={(e) => patch("city", e.target.value)}
            placeholder="Liberty"
            data-testid={`${testIdPrefix}-city`}
            disabled={disabled}
            autoComplete="address-level2"
          />
        </div>
        <div className="col-span-7 sm:col-span-3">
          {showLabels && <Label>State</Label>}
          <Select value={v.state || ""} onValueChange={(val) => patch("state", val)} disabled={disabled}>
            <SelectTrigger className="rounded-sm" data-testid={`${testIdPrefix}-state`}>
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {US_STATES.map((s) => (
                <SelectItem key={s.code} value={s.code}>
                  {s.code} — {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-5 sm:col-span-3">
          {showLabels && <Label>ZIP</Label>}
          <Input
            className="rounded-sm mono-num"
            value={v.zip_code || ""}
            onChange={(e) => patch("zip_code", e.target.value.replace(/[^0-9\-]/g, "").slice(0, 10))}
            placeholder="12754"
            inputMode="numeric"
            data-testid={`${testIdPrefix}-zip`}
            disabled={disabled}
            autoComplete="postal-code"
          />
        </div>
      </div>
    </div>
  );
}
